/*
 * 「怎么称呼你」输入框。
 *
 * ## 它不是弹层，是停在输入口位置上的一块
 *
 * 曾经是整屏遮罩 + 居中卡片，而且**新用户一进来就弹**——她还没开口说话，
 * 一个框先糊在脸上问「我该怎么称呼你呀」。问题有二：
 *
 *   ① 那句话是**她的开场白的最后一句**。开场白还没读到，框就先出现了，
 *      于是它看起来像个凭空冒出来的注册表单，而不是她在问你。
 *   ② 遮罩挡着她的立绘和那段话，用户想读也读不了。
 *
 * 现在改成：读完开场白的**最后一页**才出现，停在对话框正上方，不挡任何东西。
 *
 * ## 不可跳过，但也不靠遮罩来强制
 *
 * 这块出现时会**顶掉输入口**（舞台的「文字回复」/ 气泡的输入框），
 * 所以填完之前发不出任何消息——这就是「强制」的全部实现，不需要模态遮罩。
 *
 * ⚠ **别加「以后再说」。** 服务端的判据（`ChatEnterView.mustAskPreferredName`）
 * 是**状态**不是时机：昵称与称呼都空就一直要。跳过之后下次进来还会问，
 * 那个「以后」永远不会到来，只会变成每次进来都被拦一下。
 *
 * ⚠ 所以**失败必须留得住重试**：这个框没有退路，一旦提交失败又不能重来，
 * 用户就卡死在这儿了。错误显示在输入框下方，人可以改了名字再点一次。
 *
 * ## 为什么用输入框而不是让用户直接打字回答
 *
 * 直接回答需要她**理解并抽取**答案（「叫我小明就行」→「小明」）。
 * 这里把这一步显式化：用户确认的字面值直接落库，不经模型猜测。
 *
 * ## 没有预填
 *
 * 输入框一律从空开始。这个框只在**昵称与称呼都为空**时出现——
 * 那种情况下本来就没有任何可预填的东西。
 *
 * 曾经会拿账号昵称当预填建议，那是因为新账号会被自动分配一个占位名
 * （「候鸟3762」这种）。占位名已经取消了（`account.service.ts` 写空串），
 * 预填也就随之消失。**别再加回来**：预填一个用户没说过的名字，
 * 他多半会直接点确定，于是那个名字就成了正式称呼。
 *
 * ## 确认之后会补上账号昵称
 *
 * 提交时带 `alsoSetDisplayName: true`——用户此刻给出的是「我叫什么」，
 * 而新账号的昵称本来就是空的。服务端**只在昵称为空时才写**，
 * 不会冲掉迁移进来的 1,495 个真实昵称。
 */

import { useEffect, useRef, useState } from "react";
import { Butterfly } from "./AsyncState";

/** 与服务端 `preferredNameSchema` 的上限保持一致。 */
const MAX_CHARS = 24;

export function PreferredNamePrompt({
  characterName,
  onSubmit,
  submitting,
  error,
}: {
  characterName: string;
  onSubmit: (name: string) => void;
  submitting: boolean;
  /** 服务端拒绝的原因，例如撞了保留名单。 */
  error: string | null;
}) {
  const [name, setName] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  // ⚠ 移动端不调 focus()——那会顶起软键盘遮住半个屏幕，
  // 用户还没看清问的是什么就被键盘怼脸。
  useEffect(() => {
    const isCoarse = window.matchMedia?.("(pointer: coarse)").matches;
    if (!isCoarse) inputRef.current?.focus();
  }, []);

  const trimmed = name.trim();
  const tooLong = trimmed.length > MAX_CHARS;

  return (
    <div className="ask-name fade-in" role="group" aria-labelledby="ask-name-title">
      <p className="ask-name__title" id="ask-name-title">
        我该怎么称呼你呀？
      </p>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (trimmed && !tooLong && !submitting) onSubmit(trimmed);
        }}
      >
        <div className="ask-name__row">
          <input
            ref={inputRef}
            className={`ask-name__input${error || tooLong ? " is-bad" : ""}`}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="比如：小明"
            // maxLength 不设死，让超长可见并给出提示——
            // 悄悄截断会让用户以为自己打错了。
            enterKeyHint="done"
            disabled={submitting}
          />
          <button
            type="submit"
            className={`btn btn--primary ask-name__ok${submitting ? " is-working" : ""}`}
            disabled={!trimmed || tooLong || submitting}
          >
            {/*
              「记下…」后面紧接着还有一轮真实对话（45 秒超时），不是一次几百毫秒
              的写请求。纯文案切换在这个时长上撑不住，给一只小蝴蝶。
              实心主色底上要用 --night 那套浅色，默认的 brand-400/600 压上去看不见。
            */}
            {submitting ? (
              <>
                <Butterfly className="butterfly--night butterfly--inline" />
                记下…
              </>
            ) : (
              "就这么叫"
            )}
          </button>
        </div>

        {/* 三者共用同一格，所以不会同时出现，也就不会顶动布局。 */}
        {tooLong ? (
          <p className="ask-name__err">不能超过 {MAX_CHARS} 个字</p>
        ) : error ? (
          <p className="ask-name__err">{error}</p>
        ) : (
          <p className="ask-name__sub">
            {characterName}会一直这样叫你，也会成为你的昵称
          </p>
        )}
      </form>
    </div>
  );
}
